import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createClient, updateClient } from '../../Redux/Actions/actions';
import styles from './ClientForm.module.css';

const ClientForm = ({ client = null, onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const { user, clientsLoading, clientsError } = useSelector(state => state);
  const isEditing = !!client;

  const [formData, setFormData] = useState({
    nombre: client?.nombre || '',
    apellido: client?.apellido || '',
    telefono: client?.telefono || '',
    mail: client?.mail || '',
    categoria: client?.categoria || '',
    detalle: client?.detalle || '',
    activo: client?.activo !== undefined ? client.activo : true
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.apellido.trim()) {
      newErrors.apellido = 'El apellido es requerido';
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido';
    }

    if (!formData.mail.trim()) {
      newErrors.mail = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.mail)) {
      newErrors.mail = 'El formato del email no es válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const sellerId = user?.userId || user?.user?.id;
      console.log('ClientForm - sellerId:', sellerId, 'user object:', user);
      if (!sellerId) {
        alert('Error: No se pudo identificar al usuario. Por favor, recarga la página.');
        return;
      }

      const clientDataToSend = {
        ...formData,
        sellerId: sellerId
      };

      let saved;
      if (isEditing) {
        saved = await dispatch(updateClient(client.id, clientDataToSend));
      } else {
        saved = await dispatch(createClient(clientDataToSend));
      }

      if (onSuccess) {
        // Pasamos el cliente persistido para que el caller (p.ej. ClientSelector
        // dentro de VRCPro) pueda auto-seleccionarlo sin esperar al refetch.
        // Algunos backends devuelven el objeto envuelto en { data: { ... } }.
        const createdClient = saved?.data || saved || null;
        onSuccess(createdClient);
      }
      
      // Reset form if creating new client
      if (!isEditing) {
        setFormData({
          nombre: '',
          apellido: '',
          telefono: '',
          mail: '',
          categoria: '',
          detalle: '',
          activo: true
        });
      }
    } catch (error) {
      console.error('Error al guardar cliente:', error);
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h2>{isEditing ? 'Editar Cliente' : 'Crear Nuevo Cliente'}</h2>
        {onCancel && (
          <button 
            type="button" 
            onClick={onCancel}
            className={styles.cancelButton}
          >
            ✕
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="nombre">Nombre *</label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              className={errors.nombre ? styles.inputError : ''}
              placeholder="Ingrese el nombre"
            />
            {errors.nombre && <span className={styles.error}>{errors.nombre}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="apellido">Apellido *</label>
            <input
              type="text"
              id="apellido"
              name="apellido"
              value={formData.apellido}
              onChange={handleChange}
              className={errors.apellido ? styles.inputError : ''}
              placeholder="Ingrese el apellido"
            />
            {errors.apellido && <span className={styles.error}>{errors.apellido}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="telefono">Teléfono *</label>
            <input
              type="tel"
              id="telefono"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              className={errors.telefono ? styles.inputError : ''}
              placeholder="+54911234567"
            />
            {errors.telefono && <span className={styles.error}>{errors.telefono}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="mail">Email *</label>
            <input
              type="email"
              id="mail"
              name="mail"
              value={formData.mail}
              onChange={handleChange}
              className={errors.mail ? styles.inputError : ''}
              placeholder="ejemplo@email.com"
            />
            {errors.mail && <span className={styles.error}>{errors.mail}</span>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="categoria">Categoría</label>
          <select
            id="categoria"
            name="categoria"
            value={formData.categoria}
            onChange={handleChange}
          >
            <option value="">Seleccionar categoría</option>
            <option value="VIP">VIP</option>
            <option value="Premium">Premium</option>
            <option value="Inversor">Inversor</option>
            <option value="Primera compra">Primera compra</option>
            <option value="Regular">Regular</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="detalle">Detalles</label>
          <textarea
            id="detalle"
            name="detalle"
            value={formData.detalle}
            onChange={handleChange}
            rows="3"
            placeholder="Detalles adicionales sobre el cliente..."
          />
        </div>

        {isEditing && (
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="activo"
                checked={formData.activo}
                onChange={handleChange}
              />
              Cliente activo
            </label>
          </div>
        )}

        {clientsError && (
          <div className={styles.errorMessage}>
            {clientsError}
          </div>
        )}

        <div className={styles.buttonGroup}>
          {onCancel && (
            <button 
              type="button" 
              onClick={onCancel}
              className={styles.cancelBtn}
              disabled={clientsLoading}
            >
              Cancelar
            </button>
          )}
          <button 
            type="submit" 
            className={styles.submitBtn}
            disabled={clientsLoading}
          >
            {clientsLoading ? 'Guardando...' : (isEditing ? 'Actualizar Cliente' : 'Crear Cliente')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;